import React, { useState, useContext, FunctionComponent } from "react";
import { Button } from "@cmsgov/ds-medicare-gov";
import { ExtraSSHelp, HowDoYouGetHelp } from ".";
import {
  AppContextType,
  ActionType,
  LowIncomeSubsidyOption,
  LowIncomeSubsidyAmount,
  LowIncomeSubsidyStatus,
  FullPlanId,
} from "../../@types";
import { RouteComponentProps, withRouter } from "react-router";
import { AppContext } from "../../app/store";
import {
  getNewLISCodeFromUserOptions,
  initiateLogin,
  shouldHideExtraSSHelp,
  useCurrentPlanYear,
  useTranslate,
  useYearPart,
} from "../../helpers";
import { fetchCurrentPlan } from "../../helpers/planHelpers";
import {
  AnalyticsActionType,
  AnalyticsButtonStyle,
  AnalyticsButtonType,
} from "../../app/contexts/Analytics/types";

export const lisContinueButtonTestId = "lis-questions-lis-button";

interface LISQuestionsProps extends RouteComponentProps {
  nextPage: () => void;
}

const LISQuestions: FunctionComponent<LISQuestionsProps> = ({ nextPage }) => {
  const t = useTranslate();
  const [helpType, setHelpType] = useState<undefined | LowIncomeSubsidyOption>(
    undefined
  );
  const [amountOfSSHelp, setAmountOfSSHelp] =
    useState<LowIncomeSubsidyAmount | null>(null);
  const { state, dispatch } = useContext<AppContextType>(AppContext);
  const mctCurrentPlanYear = useCurrentPlanYear();
  const { isOpenEnrollmentNextYearOnly } = useYearPart();

  const hideExtraSSHelp = shouldHideExtraSSHelp(
    mctCurrentPlanYear,
    isOpenEnrollmentNextYearOnly
  );

  // Determines if the user has selected a Help Type that does not require further input
  const undefinitiveLowIncomeSubsidyOption = [LowIncomeSubsidyOption.Unsure];
  if (!hideExtraSSHelp) {
    undefinitiveLowIncomeSubsidyOption.push(LowIncomeSubsidyOption.SS);
  }
  const helpTypeIsDefinitive =
    helpType && !undefinitiveLowIncomeSubsidyOption.includes(helpType);

  // Calculate if we have what we need to allow the user to continue
  const canContinue =
    (helpType && helpTypeIsDefinitive) || amountOfSSHelp !== null;

  const handleContinuing = (): void => {
    const selectedLIS = getNewLISCodeFromUserOptions(
      hideExtraSSHelp,
      helpType,
      amountOfSSHelp
    );
    if (selectedLIS !== state.lis) {
      dispatch({
        type: ActionType.UPDATE_LIS,
        payload: selectedLIS,
      });
      fetchCurrentPlan(
        state.beneficiary,
        state.pharmacies,
        selectedLIS || LowIncomeSubsidyStatus.LIS_NO_HELP,
        dispatch,
        [state.nextYearCoverage as FullPlanId],
        state.futureLis
      );
    }

    nextPage();
  };

  const handleLoginClick = (): void => {
    dispatch({
      type: AnalyticsActionType.SEND_TEALIUM_EVENT,
      settings: {
        event_action: "Find Plans - Choose Plans",
        event_label: "Login",
      },
    });
    dispatch({
      type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
      settings: {
        button: {
          buttonStyle: AnalyticsButtonStyle.DEFAULT,
          buttonType: AnalyticsButtonType.BUTTON,
          text: t("logged_out_footer.login"),
        },
      },
    });
    initiateLogin(state.language, "", undefined);
  };

  return (
    <>
      <HowDoYouGetHelp
        answeredTypeOfHelp={(value: LowIncomeSubsidyOption): void =>
          setHelpType(value)
        }
      />

      {helpType &&
        helpType === LowIncomeSubsidyOption.SS &&
        !hideExtraSSHelp && (
          <ExtraSSHelp
            updateAmountOfSSHelp={(
              value: LowIncomeSubsidyAmount | null
            ): void => setAmountOfSSHelp(value)}
          />
        )}

      {helpType && helpType === LowIncomeSubsidyOption.Unsure && (
        <section data-testid="continue" className="ContinueSection">
          <Button
            onClick={handleLoginClick}
            className="ds-u-margin-bottom--4 ds-u-lg-margin-bottom--0 ds-u-md-margin-right--2 mct-u-width--full mct-u-md-width--auto"
            variation="solid"
          >
            {t("logged_out_footer.login")}
          </Button>
          <Button
            className="mct-u-width--full mct-u-md-width--auto"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Find Plans - Choose Plans",
                  event_label: "Continue without Logging in",
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.DEFAULT,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: t("landing_page.continue_without_login"),
                  },
                },
              });
              handleContinuing();
            }}
          >
            {t("landing_page.continue_without_login")}
          </Button>
        </section>
      )}

      {canContinue && (
        <section data-testid="continue" className="ContinueSection">
          <Button
            variation="solid"
            data-testid={lisContinueButtonTestId}
            className="mct-u-width--full mct-u-md-width--auto"
            onClick={(): void => {
              dispatch({
                type: AnalyticsActionType.SEND_TEALIUM_EVENT,
                settings: {
                  event_action: "Find Plans",
                  event_label: "Continue",
                },
              });
              dispatch({
                type: AnalyticsActionType.SEND_BUTTON_ENGAGEMENT_EVENT,
                settings: {
                  button: {
                    buttonStyle: AnalyticsButtonStyle.PRIMARY,
                    buttonType: AnalyticsButtonType.BUTTON,
                    text: t("landing_page.continue"),
                  },
                },
              });
              handleContinuing();
            }}
          >
            {t("landing_page.continue")}
          </Button>
        </section>
      )}
    </>
  );
};

export default withRouter(LISQuestions);
